import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { getLenis } from "../lib/motion";

interface IntroVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const INTRO_VIDEO = {
  src: "/videos/don-intro.mp4",
  poster: "/videos/don-intro-poster.jpg",
  duration: "0:52",
};

const IntroVideoModal: React.FC<IntroVideoModalProps> = ({ isOpen, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const trigger = document.activeElement as HTMLElement | null;
    const lenis = getLenis();
    const previousOverflow = document.body.style.overflow;

    lenis?.stop();
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      lenis?.start();
      trigger?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intro-video-title"
      onClick={onClose}
      data-lenis-prevent
    >
      <div
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2
            id="intro-video-title"
            className="text-white/90 text-sm md:text-base font-mono tracking-widest uppercase m-0"
          >
            <span className="text-cyan-400">Meet Don</span> — 52-second intro
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close introduction video"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-all"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="aspect-video w-full overflow-hidden rounded-2xl border border-cyan-400/30 bg-black shadow-[0_0_40px_8px_rgba(55,216,184,0.25)]">
          <video
            className="h-full w-full"
            src={INTRO_VIDEO.src}
            poster={INTRO_VIDEO.poster}
            controls
            autoPlay
            playsInline
            preload="none"
          >
            Your browser does not support embedded video.
          </video>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IntroVideoModal;
